// Renderer: MSAA direct render (no post chain), gradient pastel sky, camera-following compact shadows,
// adaptive quality. Vignette/grade moved to a free CSS overlay.
import * as THREE from 'three';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { store } from '../core/store.js';
import { bus } from '../core/bus.js';

export class WorldRenderer {
  constructor(container) {
    this.container = container;
    const w = container.clientWidth, h = container.clientHeight;

    // MSAA works because we render directly (no EffectComposer render targets).
    this.renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance', stencil: false });
    this.renderer.setSize(w, h);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap; // cheaper than PCFSoft, sharper too
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    container.appendChild(this.renderer.domElement);

    // Free "post-processing": CSS vignette + warm tint overlay. Zero GPU cost in WebGL.
    const vig = document.createElement('div');
    vig.style.cssText = 'position:absolute;inset:0;pointer-events:none;background:radial-gradient(ellipse at center, transparent 55%, rgba(20,10,0,0.22) 100%);mix-blend-mode:multiply;';
    container.appendChild(vig);

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(55, w / h, 0.5, 2000);
    this.camera.position.set(60, 60, 60);

    this._setupSky();
    this._setupLights();
    this.quality = 2; // adaptive: 2 high, 1 medium, 0 low

    // Graphics options (driven by the Settings panel via store.graphics / 'gfx:apply').
    this.autoQuality = true;
    this.densityFlex = 0;   // auto tree-density offset (±0.1) around the slider center, Auto mode only
    this._unsubGfx = bus.on('gfx:apply', () => this.applyGraphics());

    this._onResize = () => this._resize();
    window.addEventListener('resize', this._onResize);
  }

  _setupSky() {
    // Cartoon skybox: user-supplied equirectangular painting with fluffy clouds.
    this.sunPosition = new THREE.Vector3();
    this._sunSpherical(38, 125);
    // (No scene.fog — it wasn't visibly helping at play distances; removed to keep things lean.)

    new THREE.TextureLoader().load('uploads/cartoon-sky.png', (tex) => {
      tex.colorSpace = THREE.SRGBColorSpace;
      // Sky DOME instead of full-sphere equirect: the whole painting spans
      // horizon -> zenith, so ~90% of the image is visible at play angles.
      const geo = new THREE.SphereGeometry(1500, 48, 24, 0, Math.PI * 2, 0, Math.PI * 0.58);
      const mat = new THREE.MeshBasicMaterial({ map: tex, side: THREE.BackSide, fog: false, depthWrite: false });
      const dome = new THREE.Mesh(geo, mat);
      dome.frustumCulled = false;
      dome.renderOrder = -1;
      this.scene.add(dome);
      this.sky = dome;
    });
    this.renderer.setClearColor(0xcdeef2); // matches the painting's horizon, fills below the dome rim

    // Soft neutral IBL so PBR materials get gentle reflections without an HDRI download.
    const pmrem = new THREE.PMREMGenerator(this.renderer);
    this.scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    this.scene.environmentIntensity = 0.4;
    pmrem.dispose();
    bus.emit('env:ready', this.scene.environment);
  }

  _sunSpherical(elevationDeg, azimuthDeg) {
    const phi = THREE.MathUtils.degToRad(90 - elevationDeg);
    const theta = THREE.MathUtils.degToRad(azimuthDeg);
    this.sunPosition.setFromSphericalCoords(1, phi, theta);
  }

  setSunElevation(elevationDeg, azimuthDeg) {
    this._sunSpherical(elevationDeg, azimuthDeg);
    if (this.sun) this.sun.position.copy(this.sunPosition).multiplyScalar(300);
  }

  // Day/night cycle removed by request — fixed pleasant afternoon light.
  setTimeOfDay() {}

  _setupLights() {
    const sun = new THREE.DirectionalLight(0xfff3e0, 3.2);
    sun.position.copy(this.sunPosition).multiplyScalar(300);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.camera.near = 50;
    sun.shadow.camera.far = 900;
    // Compact extent that FOLLOWS the camera target (see _updateShadowCamera):
    // 90m around where you look instead of 260m fixed = sharper AND cheaper.
    const ext = 90;
    sun.shadow.camera.left = -ext; sun.shadow.camera.right = ext;
    sun.shadow.camera.top = ext; sun.shadow.camera.bottom = -ext;
    sun.shadow.bias = -0.0004;
    sun.shadow.normalBias = 0.02;
    this.scene.add(sun);
    this.scene.add(sun.target);
    this.sun = sun;

    const hemi = new THREE.HemisphereLight(0xd4f0f4, 0xbede9e, 0.95);
    this.scene.add(hemi);
    this.hemi = hemi;
  }

  // Keep the shadow box centered on what the player is looking at.
  _updateShadowCamera(focus) {
    if (!focus || !this.sun.castShadow) return;
    const f = this._shadowFocus || (this._shadowFocus = new THREE.Vector3(1e9, 0, 0));
    // only re-render shadows when the focus moved meaningfully
    if (f.distanceToSquared(focus) < 25) return;
    // Throttle full shadow re-renders to ~10/sec while the camera pans (a shadow pass over ~1.3M tris
    // every frame was the 1%-low spike). Shadows trail a hair during fast pans and snap when you stop.
    const now = performance.now();
    if (this._shadowT && now - this._shadowT < 100) return;
    this._shadowT = now;
    f.copy(focus);
    this.sun.target.position.copy(focus);
    this.sun.position.copy(focus).addScaledVector(this.sunPosition, 300);
    this.renderer.shadowMap.needsUpdate = true;
  }

  _resize() {
    const w = this.container.clientWidth, h = this.container.clientHeight;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
  }

  render(focus) {
    this._frame = (this._frame || 0) + 1;

    // Adaptive quality: measure fps, step down/up automatically to hit 60.
    const now = performance.now();
    if (this._fpsT == null) { this._fpsT = now; this._fpsN = 0; }
    this._fpsN++;
    const _fdt = now - (this._lastFrameNow || now); this._lastFrameNow = now; this._worstDt = Math.max(this._worstDt || 0, _fdt);
    if (now - this._fpsT >= 1000) {
      const fps = this._fpsN * 1000 / (now - this._fpsT);
      this.fps = Math.round(fps);
      this._fpsT = now; this._fpsN = 0;
      if (this.autoQuality) {
        this._peakFps = Math.max(fps, (this._peakFps || 60) * 0.99);
        const capv = (store.get().graphics && store.get().graphics.fpsCap) || 0;
        const tgt = capv > 0 ? Math.min(capv, this._peakFps) : this._peakFps;
        const lo = tgt * 0.66, hi = tgt * 0.92;
        const low1 = 1000 / Math.max(this._worstDt || 16, 1); this._worstDt = 0;   // 1%-low proxy (worst frame this window)
        const pressure = fps < lo || low1 < lo * 0.7;   // low average OR bad frame-time spike (stutter)
        const headroom = fps > hi && low1 > hi * 0.55;
        if (pressure && this.quality > 0) this.setQuality(this.quality - 1);
        else if (headroom && this.quality < 2) {
          this._stable = (this._stable || 0) + 1;
          if (this._stable >= 5) { this.setQuality(this.quality + 1); this._stable = 0; }
        } else this._stable = 0;
        if (pressure) this.densityFlex = Math.max(-0.1, this.densityFlex - 0.05);
        else if (headroom) this.densityFlex = Math.min(0.1, this.densityFlex + 0.05);
        // NOTE: auto-quality NO LONGER touches shadows. Resizing/disposing the shadow map at runtime
        // invalidates the compiled program of every shadow-RECEIVING material (terrain/buildings/animals/
        // trees) → a one-frame recompile where they submit but don't rasterize = the "only the water
        // shows" glitch on an auto graphics switch. Shadow resolution is a MANUAL-only setting now
        // (applyShadow); auto tunes resolution scale, tree-density and LOD — none recompile receivers.
      }
    }

    // Shadows: render-on-demand — baked after load; refreshed only when the view moves
    // or at a low tick so the tree-sway shadows stay subtly alive without GPU cost.
    if (this.sun.castShadow) {
      this.renderer.shadowMap.autoUpdate = false;
      this._updateShadowCamera(focus);
      const interval = this.quality === 2 ? 12 : 30; // 5Hz / 2Hz — imperceptible, near-free
      if (this._frame % interval === 0) this.renderer.shadowMap.needsUpdate = true;
    }

    // Temporal quality: while the camera moves (WASD/pan/zoom/rotate), drop resolution; restore when still.
    const cp = this.camera.position, cq = this.camera.quaternion;
    const moved = !this._lastCP || this._lastCP.distanceToSquared(cp) > 1e-4 || (this._lastCQ ? Math.abs(this._lastCQ.dot(cq)) < 0.999995 : true);
    (this._lastCP = this._lastCP || new THREE.Vector3()).copy(cp);
    (this._lastCQ = this._lastCQ || new THREE.Quaternion()).copy(cq);
    if (moved) this._inputT = now;
    const moving = now - (this._inputT || 0) < 180;
    if (moving && !this._movingLow) { this._movingLow = true; this._basePR = this.renderer.getPixelRatio(); this.renderer.setPixelRatio(Math.max(0.6, this._basePR * 0.6)); }
    else if (!moving && this._movingLow) { this._movingLow = false; this.renderer.setPixelRatio(this._basePR); }

    this.renderer.render(this.scene, this.camera);
  }

  // Apply the Settings-panel graphics options (Auto vs manual quality) from the store.
  applyGraphics() {
    const g = store.get().graphics || {};
    this.autoQuality = g.auto !== false;
    if (!this.autoQuality && typeof g.quality === 'number') this.setQuality(g.quality);
    this.applyShadow();
  }

  // Quality tiers: 2 = high, 1 = medium (1.15x ratio, 1024 shadows), 0 = low (1x, no shadows)
  setQuality(q) {
    if (this.quality === q) return;
    this.quality = q;
    const dpr = window.devicePixelRatio || 1;
    if (q === 2) this.renderer.setPixelRatio(Math.min(dpr, 1.5));
    else if (q === 1) this.renderer.setPixelRatio(Math.min(dpr, 1.15));
    else this.renderer.setPixelRatio(1);
    this._resize();
    bus.emit('ui:toast', { text: q === 2 ? '⚡ Graphics: high' : q === 1 ? '⚡ Graphics: medium (auto)' : '⚡ Graphics: fast (auto)', kind: 'info' });
  }

  // Shadow-map resolution is a manual setting (Off / 1024 / 2048). Cartoon style → forgiving.
  applyShadow() {
    const res = store.get().graphics && store.get().graphics.shadowRes;
    const r = res == null ? 1024 : res;
    this._shadowIdx = 0;   // reset to the ceiling whenever the setting changes
    this._applyShadowRes(r);
  }
  _applyShadowRes(r) {
    this.sun.castShadow = r > 0;
    if (r > 0 && this.sun.shadow.mapSize.width !== r) { this.sun.shadow.mapSize.set(r, r); this.sun.shadow.map?.dispose(); this.sun.shadow.map = null; }
  }

  dispose() {
    this._unsubGfx?.();
    window.removeEventListener('resize', this._onResize);
    this.sun?.shadow?.map?.dispose?.();
    this.scene.environment?.dispose?.();
    this.renderer.dispose();
    if (this.renderer.domElement?.parentNode) this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
  }
}
